/**
 * The `create_branch` agent-action lifecycle capability — the approval-gated
 * path out of protected main. A write-capable agent on main proposes a
 * branch via `propose_create_branch`; approving the card creates + opens
 * the branch server-side, and this capability then SWITCHES the tab's
 * active branch onto it so the agent's follow-up write proposals apply
 * against the new branch instead of being refused.
 *
 * Importing this module registers the capability as a side effect, like
 * the cypher-write and ServiceNow capabilities.
 */
import { applyBranchSwitch } from '../../active-branch'
import { agentWritesApi } from '../../api/agents/agent-writes'
import type { InFlightAgentActionProposal } from '../../slices/agent-orchestrator-slice'
import {
  buildCreateBranchEntryFromToolInput,
  scheduleHydratePendingAgentWrites
} from '../cypher-write-pending'
import {
  readStructuredMutationError,
  registerAgentActionLifecycle,
  type AgentActionApplyOutcome,
  type AgentActionCompleteHydration,
  type AgentActionLifecycleCapability,
  type AgentActionRejectOutcome
} from './lifecycle-registry'

const CREATE_BRANCH_TOOL_NAME = 'propose_create_branch'

function readString(record: Record<string, unknown>, key: string): string | null {
  const v = record[key]
  return typeof v === 'string' ? v : null
}

function readNumber(record: Record<string, unknown>, key: string): number | undefined {
  const v = record[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

/** Parse the propose output's async-suspend envelope (single-row plans). */
function readProposeOutput(
  parentToolUseId: string,
  output: unknown
): AgentActionCompleteHydration | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) return null
  const obj = output as Record<string, unknown>
  if (obj.pending !== true) return null
  const planId = readString(obj, 'plan_id')
  if (planId === null) return null
  const rawResults = obj.results
  if (!Array.isArray(rawResults)) return null
  const results: Array<{ toolUseId: string; writeId: string; index?: number }> = []
  for (const raw of rawResults) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
    const r = raw as Record<string, unknown>
    const toolUseId = readString(r, 'tool_use_id')
    const writeId = readString(r, 'write_id')
    if (toolUseId === null || writeId === null) continue
    results.push({ toolUseId, writeId, index: readNumber(r, 'index') })
  }
  return { parentToolUseId, planId, results }
}

/** Strip double quotes so the summary survives the resume-bubble row regex. */
function sanitizeSummary(text: string): string {
  return text.replace(/"/g, '').trim()
}

export const createBranchLifecycleCapability: AgentActionLifecycleCapability = {
  kind: 'create_branch',
  toolName: CREATE_BRANCH_TOOL_NAME,

  parseToolStart(toolUseId, input, startedAtMs) {
    const entry = buildCreateBranchEntryFromToolInput(toolUseId, input, startedAtMs)
    return entry ? [entry] : []
  },

  parseToolComplete(toolUseId, output) {
    return readProposeOutput(toolUseId, output)
  },

  scheduleHydratePending(dispatch, conversationId) {
    scheduleHydratePendingAgentWrites(dispatch, conversationId)
  },

  apply(toolUseId) {
    return async (dispatch): Promise<AgentActionApplyOutcome> => {
      try {
        const result = await dispatch(
          agentWritesApi.endpoints.applyCypherWrite.initiate({
            toolUseId,
            graphAffecting: false
          })
        ).unwrap()
        if (result.ok) {
          const created = result.result ?? {}
          const id = typeof created.id === 'string' ? created.id : undefined
          const name = typeof created.name === 'string' ? created.name : undefined
          // The branch was created + opened server-side; adopt it as
          // the active branch so the writes the agent proposes next
          // (and any the user approves right after) land on it.
          if (id) {
            applyBranchSwitch(dispatch, id)
          }
          return { kind: 'success', settledMeta: { createdBranch: { id, name } } }
        }
        return { kind: 'apply_failed', error: result.error ?? 'Create branch failed.' }
      } catch (err) {
        // HTTP 409 with `{ ok: false, error }` is a structured refusal,
        // not a transport blip — settle as terminal.
        const refused = readStructuredMutationError(err)
        if (refused !== null) {
          return { kind: 'apply_failed', error: refused.error }
        }
        const message = err instanceof Error ? err.message : 'Create branch failed.'
        return { kind: 'transport_error', error: message }
      }
    }
  },

  reject(toolUseId, reason) {
    return async (dispatch): Promise<AgentActionRejectOutcome> => {
      try {
        const result = await dispatch(
          agentWritesApi.endpoints.rejectCypherWrite.initiate({ toolUseId, reason })
        ).unwrap()
        if (result.ok) {
          return { kind: 'success', settledMeta: { rejectionReason: result.reason ?? reason } }
        }
        return { kind: 'reject_failed', error: result.error ?? 'Reject failed.' }
      } catch (err) {
        const refused = readStructuredMutationError(err)
        if (refused !== null) {
          return { kind: 'reject_failed', error: refused.error }
        }
        const message = err instanceof Error ? err.message : 'Reject failed.'
        return { kind: 'transport_error', error: message }
      }
    }
  },

  buildResumeRowLine(proposal: InFlightAgentActionProposal, displayNumber) {
    if (proposal.kind !== 'create_branch') {
      return `${displayNumber}. UNDECIDED — "${sanitizeSummary(proposal.summary)}".`
    }
    const summary = sanitizeSummary(proposal.summary || `Create branch ${proposal.branchName}`)
    const terminal = proposal.terminalState
    if (terminal?.kind === 'applied') {
      const created = proposal.createdBranch
      const tail = created?.name
        ? `; branch ${created.name} created and is now the user's active branch — propose your graph writes now`
        : '; branch created and active'
      return `${displayNumber}. APPLIED — "${summary}"${tail}.`
    }
    if (terminal?.kind === 'apply_failed') {
      return `${displayNumber}. APPLY_FAILED — "${summary}"; error: ${terminal.error}.`
    }
    if (terminal?.kind === 'rejected') {
      const reason = proposal.rejectionReason || 'rejected by user'
      return `${displayNumber}. REJECTED — "${summary}"; reason: "${reason}". The user declined to create a branch; main is protected, so no graph writes can apply until they create or switch to a branch.`
    }
    return `${displayNumber}. UNDECIDED — "${summary}".`
  }
}

registerAgentActionLifecycle(createBranchLifecycleCapability)
